import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Papa from "papaparse"; // Added for Bulk CSV Upload

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const PRODUCT_API = `${API_BASE_URL}/api/products`;
const CATEGORY_API = `${API_BASE_URL}/api/categories`;

// Helper to format image URLs correctly for display
const resolveImageUrl = (img) => {
  if (!img) return "";
  if (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:")) return img;
  let cleanPath = img.startsWith("/uploads/") ? img.replace("/uploads/", "") : img.replace("uploads/", "");
  const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  return `${baseUrl}/uploads/${cleanPath}`;
};

// --- MODERN TOGGLE SWITCH COMPONENT ---
const ToggleSwitch = ({ isOn, onToggle, label }) => {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "36px",
          height: "20px",
          borderRadius: "10px",
          backgroundColor: isOn ? "#10b981" : "#d1d5db", // Green when ON, Grey when OFF
          border: "none",
          position: "relative",
          cursor: "pointer",
          transition: "background-color 0.3s ease",
          padding: 0,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "white",
            position: "absolute",
            top: "2px",
            left: isOn ? "18px" : "2px",
            transition: "left 0.3s ease",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        />
      </button>
      {label && <span style={{ fontSize: "13px", color: "#4b5563" }}>{label}</span>}
    </div>
  );
};
// --------------------------------------

const initialForm = {
  name: "",
  sku: "",
  description: "",
  price: "",
  originalPrice: "",
  category: "",
  brand: "",
  stock: "",
  tags: "",
  weight: "",
  discount: "",
  isFeatured: false,
  isDeal: false,
  isTrending: false,
  isActive: true,
  existingImages: [],
  newImages: [],
};

function ProductsManager() {
  const token = localStorage.getItem("token");

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);

  const authConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(PRODUCT_API, {
        params: { search, category: selectedCategory },
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(data?.products || data || []);
    } catch (err) {
      console.error(err);
      toast.error("Unable to fetch products.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(CATEGORY_API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(data?.categories || data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [search, selectedCategory]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setForm((prev) => ({
      ...prev,
      newImages: [...prev.newImages, ...files],
    }));
    e.target.value = "";
  };

  const removeExistingImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  const removeNewImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      newImages: prev.newImages.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(initialForm);
    setShowModal(true);
  };

  const openEdit = (product) => {
    setEditingId(product._id || product.id);
    setForm({
      name: product.name || "",
      sku: product.sku || "",
      description: product.description || "",
      price: product.price || "",
      originalPrice: product.originalPrice || "",
      category: product.category?._id || product.category || "",
      brand: product.brand || "",
      stock: product.stock || "",
      tags: Array.isArray(product.tags) ? product.tags.join(", ") : product.tags || "",
      weight: product.weight || "",
      discount: product.discount || "",
      isFeatured: !!product.isFeatured,
      isDeal: !!product.isDeal,       // <-- FIXED: Properly maps existing database value
      isTrending: !!product.isTrending, // <-- FIXED: Properly maps existing database value
      isActive: product.isActive !== false,
      existingImages: product.images || [],
      newImages: [],
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const formData = new FormData();
      
      formData.append("name", form.name);
      formData.append("sku", form.sku);
      formData.append("description", form.description);
      formData.append("price", form.price);
      formData.append("originalPrice", form.originalPrice);
      formData.append("category", form.category);
      formData.append("brand", form.brand);
      formData.append("stock", form.stock);
      formData.append("tags", form.tags);
      formData.append("weight", form.weight);
      formData.append("discount", form.discount);
      formData.append("isFeatured", form.isFeatured);
      formData.append("isDeal", form.isDeal);
      formData.append("isTrending", form.isTrending);
      formData.append("isActive", form.isActive);

      form.existingImages.forEach((imgUrl) => {
        formData.append("existingImages", imgUrl);
      });

      form.newImages.forEach((file) => {
        formData.append("images", file);
      });

      if (editingId) {
        await axios.put(`${PRODUCT_API}/${editingId}`, formData, authConfig);
        toast.success("Product updated successfully.");
      } else {
        await axios.post(PRODUCT_API, formData, authConfig);
        toast.success("Product created successfully.");
      }

      closeModal();
      fetchProducts();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Unable to save product.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this product? This action cannot be undone.");
    if (!confirmed) return;

    try {
      await axios.delete(`${PRODUCT_API}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Product deleted successfully.");
      fetchProducts();
    } catch (err) {
      console.error(err);
      toast.error("Unable to delete product.");
    }
  };

const handleToggleStatus = async (product) => {
    try {
      await axios.patch(
        `${PRODUCT_API}/${product._id || product.id}/status`,
        { isActive: !product.isActive },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json" // <-- ADDED FOR SAFETY
          } 
        }
      );
      toast.success(`Product is now ${!product.isActive ? 'Active' : 'Inactive'}`);
      
      setProducts(products.map(p => 
        (p._id || p.id) === (product._id || product.id) 
          ? { ...p, isActive: !p.isActive } 
          : p
      ));
    } catch (err) {
      console.error(err);
      toast.error("Unable to update product status.");
    }
  };

  const handleTogglePlacement = async (product, field) => {
    try {
      await axios.patch(
        `${PRODUCT_API}/${product._id || product.id}/placement`,
        { [field]: !product[field] },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json" // <-- ADDED FOR SAFETY
          } 
        }
      );
      
      setProducts(products.map(p => 
        (p._id || p.id) === (product._id || product.id) 
          ? { ...p, [field]: !p[field] } 
          : p
      ));
    } catch (err) {
      console.error(err);
      toast.error("Unable to update product visibility.");
    }
  };

  // --- BULK UPLOAD HANDLER ---
  const handleBulkUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          setLoading(true);
          
          const formattedProducts = results.data.map((item) => ({
            name: item.name,
            sku: item.sku,
            price: Number(item.price),
            originalPrice: Number(item.originalPrice) || 0,
            stock: Number(item.stock) || 10,
            category: item.categoryId || item.category, 
            brand: item.brand || "",
            description: item.description || "",
            isActive: true,
          }));

          const { data } = await axios.post(
            `${PRODUCT_API}/bulk`,
            { products: formattedProducts },
            { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
          );

          toast.success(data.message || "Bulk upload successful!");
          fetchProducts();
        } catch (err) {
          console.error(err);
          toast.error(err.response?.data?.message || "Failed to bulk upload products.");
        } finally {
          setLoading(false);
          e.target.value = null; // Reset file input
        }
      },
    });
  };

  return (
    <div className="manager-section">
      <div className="manager-topbar">
        <div>
          <h2>Products</h2>
          <p>Manage product details, pricing, stock, images, and visibility.</p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          
          {/* BULK UPLOAD BUTTON */}
          <label className="outline-btn" style={{ cursor: "pointer", padding: "8px 16px", margin: 0, display: "inline-flex", alignItems: "center" }}>
            Bulk Import CSV
            <input 
              type="file" 
              accept=".csv" 
              style={{ display: "none" }} 
              onChange={handleBulkUpload} 
            />
          </label>
          
          <button className="theme-btn" onClick={openCreate}>
            Add Product
          </button>
        </div>
      </div>

      <div className="manager-tools">
        <div className="manager-search-wrap">
          <input
            type="text"
            className="manager-search"
            placeholder="Search products by name, SKU, brand..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="manager-filter-wrap">
          <select
            className="manager-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat._id || cat.id} value={cat._id || cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="manager-table-wrap">
        <table className="manager-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Category</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Status</th>
              <th>Show In</th>
              <th>Images</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center", padding: "2rem" }}>Loading products...</td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan="9">
                  <div className="empty-state">No products found.</div>
                </td>
              </tr>
            ) : (
              products.map((item) => (
                <tr key={item._id || item.id}>
                  <td>
                    <strong>{item.name}</strong>
                    <div style={{ fontSize: "0.85em", color: "#666" }}>{item.brand || "-"}</div>
                  </td>
                  <td>{item.sku || "-"}</td>
                  <td>{item.category?.name || item.categoryName || "-"}</td>
                  <td>₹{item.price}</td>
                  <td>{item.stock ?? "-"}</td>
                  <td>
                    <ToggleSwitch 
                      isOn={item.isActive} 
                      onToggle={() => handleToggleStatus(item)} 
                      label={item.isActive ? "Active" : "Inactive"} 
                    />
                  </td>
                  <td>
                    <div className="placement-toggles" style={{ display: "flex", flexDirection: "column" }}>
                      <ToggleSwitch 
                        isOn={item.isFeatured} 
                        onToggle={() => handleTogglePlacement(item, "isFeatured")} 
                        label="Featured" 
                      />
                      <ToggleSwitch 
                        isOn={item.isDeal} 
                        onToggle={() => handleTogglePlacement(item, "isDeal")} 
                        label="Deals" 
                      />
                      <ToggleSwitch 
                        isOn={item.isTrending} 
                        onToggle={() => handleTogglePlacement(item, "isTrending")} 
                        label="Trending" 
                      />
                    </div>
                  </td>
                  <td>{item.images?.length || 0}</td>
                  <td>
                    <div className="table-actions">
                      <button className="outline-btn" onClick={() => openEdit(item)}>Edit</button>
                      <button className="danger-btn" onClick={() => handleDelete(item._id || item.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: "800px" }}>
            <div className="modal-head">
              <div>
                <h3>{editingId ? "Edit Product" : "Add Product"}</h3>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                
                <div className="form-group full" style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "8px" }}>
                  <label>Product Images</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="manager-input"
                    onChange={handleImagesChange}
                  />

                  {form.existingImages.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      <p style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>Current Images (Click X to remove):</p>
                      <div className="image-preview-grid" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {form.existingImages.map((imgUrl, index) => (
                          <div key={`existing-${index}`} style={{ position: "relative", width: "80px", height: "80px" }}>
                            <img 
                              src={resolveImageUrl(imgUrl)} 
                              alt="existing" 
                              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }} 
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              style={{ position: "absolute", top: "-5px", right: "-5px", background: "red", color: "white", border: "none", borderRadius: "50%", width: "20px", height: "20px", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {form.newImages.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      <p style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>New Images to Upload:</p>
                      <div className="image-preview-grid" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {form.newImages.map((file, index) => (
                          <div key={`new-${index}`} style={{ position: "relative", width: "80px", height: "80px" }}>
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt="new preview" 
                              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px", border: "2px solid #4CAF50" }} 
                            />
                            <button
                              type="button"
                              onClick={() => removeNewImage(index)}
                              style={{ position: "absolute", top: "-5px", right: "-5px", background: "red", color: "white", border: "none", borderRadius: "50%", width: "20px", height: "20px", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Product Name *</label>
                  <input className="manager-input" name="name" value={form.name} onChange={handleChange} required />
                </div>

                <div className="form-group">
                  <label>SKU *</label>
                  <input className="manager-input" name="sku" value={form.sku} onChange={handleChange} placeholder="SKU-001" required />
                </div>

                <div className="form-group full">
                  <label>Description</label>
                  <textarea className="manager-textarea" name="description" value={form.description} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Price (₹) *</label>
                  <input type="number" className="manager-input" name="price" value={form.price} onChange={handleChange} required />
                </div>

                <div className="form-group">
                  <label>Original Price (₹)</label>
                  <input type="number" className="manager-input" name="originalPrice" value={form.originalPrice} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select className="manager-select" name="category" value={form.category} onChange={handleChange} required>
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Brand</label>
                  <input className="manager-input" name="brand" value={form.brand} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Stock</label>
                  <input type="number" className="manager-input" name="stock" value={form.stock} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Discount (%)</label>
                  <input type="number" className="manager-input" name="discount" value={form.discount} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Weight</label>
                  <input className="manager-input" name="weight" value={form.weight} onChange={handleChange} placeholder="500g" />
                </div>

                <div className="form-group full">
                  <label>Tags (comma separated)</label>
                  <input className="manager-input" name="tags" value={form.tags} onChange={handleChange} placeholder="new, trending, premium" />
                </div>

                <div className="form-group full">
                  <div className="checkbox-row" style={{ display: "flex", gap: "15px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <input type="checkbox" name="isFeatured" checked={form.isFeatured} onChange={handleChange} /> Featured
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <input type="checkbox" name="isDeal" checked={form.isDeal} onChange={handleChange} /> Deals
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <input type="checkbox" name="isTrending" checked={form.isTrending} onChange={handleChange} /> Trending
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} /> Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="outline-btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="theme-btn" disabled={submitting}>
                  {submitting ? "Saving..." : editingId ? "Update Product" : "Create Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsManager;